import React, { useCallback, useEffect, useRef, useState } from "react";
import { Box, Typography, TextField, MenuItem, Select, FormControl, InputLabel, Button, Chip, CircularProgress } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import SendIcon from "@mui/icons-material/Send";
import ScheduleSendIcon from "@mui/icons-material/ScheduleSend";
import { toast } from "react-toastify";
import { C } from "./reportingUtils";
import { listReportSchedules, createReportSchedule, deleteReportSchedule, sendReportScheduleNow } from "../api/underwritingApi";
import { createEmailActionRunner, emailActionOutcome } from "../utils/emailAction";

const CADENCES = [{ value: "daily", label: "Daily" }, { value: "weekly", label: "Weekly" }, { value: "monthly", label: "Monthly" }];

const SchedulesPanel = ({ tenantId }) => {
  const [schedules, setSchedules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState("Portfolio export");
  const [cadence, setCadence] = useState("weekly");
  const [recipients, setRecipients] = useState("");
  const [busy, setBusy] = useState(false);
  const [sendingId, setSendingId] = useState(null);
  const runEmailAction = useRef(createEmailActionRunner());

  const load = useCallback(async () => {
    if (!tenantId) return;
    setLoading(true);
    try { setSchedules(await listReportSchedules(tenantId)); }
    catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [tenantId]);

  useEffect(() => { load(); }, [load]);

  const create = async () => {
    const emails = recipients.split(/[,\s;]+/).map((e) => e.trim()).filter(Boolean);
    if (emails.length === 0) { toast.info("Add at least one recipient email."); return; }
    setBusy(true);
    try {
      await createReportSchedule(tenantId, { name: name.trim() || "Portfolio export", cadence, recipients: emails });
      toast.success("Schedule created.");
      setRecipients("");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.message || "Couldn't create the schedule.");
    } finally { setBusy(false); }
  };

  const remove = async (id) => { try { await deleteReportSchedule(tenantId, id); await load(); } catch (e) { toast.error("Couldn't delete."); } };
  const sendNow = async (id) => {
    setSendingId(id);
    try {
      const result = await runEmailAction.current({
        tenantId, action: `report-schedule:${id}`, payload: { id },
        request: ({ eventId }) => sendReportScheduleNow(tenantId, id, { eventId }),
      });
      const outcome = emailActionOutcome(result, "Report");
      toast[outcome.status](outcome.message);
      await load();
    } catch (e) { toast.error(e?.response?.data?.message || e?.message || "Couldn't send."); }
    finally { setSendingId(null); }
  };

  const fmtDate = (iso) => (iso ? new Date(iso).toLocaleString() : "—");

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: "14px" }}>
      <Box sx={{ background: "#fff", border: `1px solid ${C.line}`, borderRadius: "12px", p: "16px 18px" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
          <ScheduleSendIcon sx={{ color: C.blue, fontSize: 20 }} />
          <Typography sx={{ fontSize: 15, fontWeight: 700, color: C.ink }}>Scheduled email exports</Typography>
        </Box>
        <Typography sx={{ fontSize: 12.5, color: C.muted, mb: 1.5 }}>The full portfolio (CSV) is emailed to the recipients on your chosen cadence.</Typography>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.25, alignItems: "center" }}>
          <TextField label="Report name" size="small" value={name} onChange={(e) => setName(e.target.value)} sx={{ minWidth: 190 }} />
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel sx={{ fontSize: 13 }}>Cadence</InputLabel>
            <Select label="Cadence" value={cadence} onChange={(e) => setCadence(e.target.value)} sx={{ fontSize: 13.5, background: "#fff" }}>
              {CADENCES.map((c) => <MenuItem key={c.value} value={c.value} sx={{ fontSize: 13.5 }}>{c.label}</MenuItem>)}
            </Select>
          </FormControl>
          <TextField label="Recipients (comma-separated)" size="small" value={recipients} onChange={(e) => setRecipients(e.target.value)} sx={{ flex: 1, minWidth: 260 }} placeholder="risk@company.com, ops@company.com" />
          <Button startIcon={<AddIcon />} variant="contained" onClick={create} disabled={busy} sx={{ textTransform: "none", background: C.blue, "&:hover": { background: "#2455c0" } }}>Add schedule</Button>
        </Box>
      </Box>

      <Box sx={{ display: "flex", flexDirection: "column", gap: "8px" }}>
        {loading && <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}><CircularProgress size={24} /></Box>}
        {!loading && schedules.length === 0 && <Typography sx={{ fontSize: 13, color: C.muted, textAlign: "center", py: 3 }}>No scheduled exports yet.</Typography>}
        {schedules.map((s) => (
          <Box key={s.id} sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2, background: "#fff", border: `1px solid ${C.line}`, borderRadius: "10px", p: "12px 16px" }}>
            <Box sx={{ minWidth: 0 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                <Typography sx={{ fontSize: 13.5, fontWeight: 700, color: C.ink }}>{s.name}</Typography>
                <Chip label={s.cadence} size="small" sx={{ height: 19, fontSize: 10.5, fontWeight: 700, textTransform: "capitalize", background: "#EEF4FF", color: C.blue }} />
              </Box>
              <Typography sx={{ fontSize: 12.5, color: C.muted, mt: "2px" }}>
                To {s.recipients?.join(", ")} · next {fmtDate(s.nextRunAt)}{s.lastRunAt ? ` · last run ${fmtDate(s.lastRunAt)}` : ""}
              </Typography>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, flex: "0 0 auto" }}>
              <Button size="small" startIcon={<SendIcon sx={{ fontSize: 15 }} />} onClick={() => sendNow(s.id)} disabled={sendingId !== null || !tenantId} sx={{ textTransform: "none", color: C.ink }}>{sendingId === s.id ? "Sending…" : "Send now"}</Button>
              <Button size="small" onClick={() => remove(s.id)} sx={{ minWidth: 0, color: C.muted }}><DeleteOutlineIcon fontSize="small" /></Button>
            </Box>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export default SchedulesPanel;
